import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  BarChart3Icon,
  CalendarIcon,
  ChevronLeftIcon,
  ChevronDownIcon,
  CheckCircle2Icon,
  DownloadIcon,
  EllipsisIcon,
  Loader2Icon,
  PencilIcon,
  PlusIcon,
  QrCodeIcon,
  SearchIcon,
  Settings2Icon,
  XIcon,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { downloadLeadsAsXlsx } from '@/lib/lead-export';
import {
  apiBaseUrl,
  authClient,
  getSalonStats,
  listLeadsByRegion,
  listRegions,
  listSalonsByRegion,
  listUserLeads,
  listUserRegions,
  updateSalon,
  type LeadRecord,
  type RegionRecord,
  type SalonRecord,
  type SalonStats,
} from '@/lib/auth-client';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SortHeaderButton, ToolbarButton } from '@/components/ui/table-shared';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { ConfirmationModal } from './confirmation-modal';
import { Input } from '@/components/ui/input';
import { SalonForm } from './salon-form';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { UsersTableColumnsPopover, type UsersTableColumnOption } from '@/components/users-table-columns-popover';

type SalonListStatusFilter = 'all' | SalonRecord['status'];
type SalonSortKey = 'contacts' | 'gainRate' | 'lots' | 'name' | 'region' | 'startDate' | 'status' | 'type';
type SortDirection = 'asc' | 'desc';
type SalonSortPreset = `${SalonSortKey}:${SortDirection}`;

type ConfirmState = {
  confirmText: string;
  description: string;
  isOpen: boolean;
  onConfirm: () => Promise<void>;
  title: string;
};

type SalonMetrics = {
  contactsCollected: number;
  lot1Distributed: number;
  lot2Distributed: number;
  lotsDedicated: number;
  lotsWon: number;
};

const pageSizeOptions = [20, 50, 100, 250] as const;
const SALONS_TABLE_COLUMNS_STORAGE_KEY = 'vibecode-admin-salons-table-columns';
const sortPresetOptions: Array<{ label: string; value: SalonSortPreset }> = [
  { label: 'Ordre personnalisé', value: 'name:asc' },
  { label: 'Nom (A-Z)', value: 'name:asc' },
  { label: 'Nom (Z-A)', value: 'name:desc' },
  { label: 'Date la plus proche', value: 'startDate:asc' },
  { label: 'Date la plus récente', value: 'startDate:desc' },
  { label: 'Région (A-Z)', value: 'region:asc' },
  { label: 'Région (Z-A)', value: 'region:desc' },
  { label: 'Contacts collectés', value: 'contacts:desc' },
  { label: 'Type (A-Z)', value: 'type:asc' },
  { label: 'Type (Z-A)', value: 'type:desc' },
  { label: 'Lots (croissant)', value: 'lots:asc' },
  { label: 'Lots (décroissant)', value: 'lots:desc' },
  { label: '% gains (croissant)', value: 'gainRate:asc' },
  { label: '% gains (décroissant)', value: 'gainRate:desc' },
  { label: 'Statuts actifs d’abord', value: 'status:asc' },
];

type SalonTableColumnKey = 'name' | 'date' | 'region' | 'contacts' | 'type' | 'lots' | 'gainRate' | 'status';

const defaultTableColumns: Array<UsersTableColumnOption & { key: SalonTableColumnKey }> = [
  { key: 'name', label: 'Nom du salon', visible: true },
  { key: 'date', label: 'Date', visible: true },
  { key: 'region', label: 'Région', visible: true },
  { key: 'contacts', label: 'Contacts collectés', visible: true },
  { key: 'type', label: 'Type', visible: true },
  { key: 'lots', label: 'Lots', visible: true },
  { key: 'gainRate', label: '% gains', visible: true },
  { key: 'status', label: 'Statut', visible: true },
];

function readStoredTableColumns() {
  if (typeof window === 'undefined') {
    return defaultTableColumns;
  }

  try {
    const raw = window.localStorage.getItem(SALONS_TABLE_COLUMNS_STORAGE_KEY);
    if (!raw) {
      return defaultTableColumns;
    }

    const parsed = JSON.parse(raw) as Array<Partial<UsersTableColumnOption> & { key?: string }>;
    const defaults = new Map(defaultTableColumns.map((column) => [column.key, column]));
    const normalized = parsed
      .map((column) => {
        if (!column.key) return null;
        const defaultColumn = defaults.get(column.key as SalonTableColumnKey);
        if (!defaultColumn) return null;
        return {
          ...defaultColumn,
          visible: typeof column.visible === 'boolean' ? column.visible : defaultColumn.visible,
        };
      })
      .filter((column): column is (typeof defaultTableColumns)[number] => column !== null);

    const missing = defaultTableColumns.filter((column) => !normalized.some((item) => item.key === column.key));
    const merged = [...normalized, ...missing];
    if (merged.every((column) => !column.visible)) {
      return defaultTableColumns;
    }
    return merged;
  } catch {
    return defaultTableColumns;
  }
}

function getPaginationItems(page: number, pageCount: number) {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  const pages = new Set<number>([1, pageCount, page, page - 1, page + 1]);

  if (page <= 3) {
    pages.add(2);
    pages.add(3);
    pages.add(4);
  }

  if (page >= pageCount - 2) {
    pages.add(pageCount - 1);
    pages.add(pageCount - 2);
    pages.add(pageCount - 3);
  }

  return Array.from(pages).filter((value) => value >= 1 && value <= pageCount).sort((left, right) => left - right);
}

function getSortPresetLabel(sortKey: SalonSortKey, sortDirection: SortDirection) {
  const preset = `${sortKey}:${sortDirection}` as SalonSortPreset;
  return sortPresetOptions.find((option) => option.value === preset)?.label ?? 'Ordre personnalisé';
}

function formatDateRange(startDate?: string | null, endDate?: string | null) {
  if (!startDate && !endDate) {
    return 'Dates à définir';
  }

  const formatter = new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const start = startDate ? formatter.format(new Date(startDate)) : '...';
  const end = endDate ? formatter.format(new Date(endDate)) : '...';

  return `${start} — ${end}`;
}

function formatSalonStatus(status: SalonRecord['status']) {
  if (status === 'Planifié') return 'Planifié';
  if (status === 'Active') return 'Actif';
  if (status === 'Inactive') return 'Inactif';
  return 'Terminé';
}

function getSalonStatusClasses(status: SalonRecord['status']) {
  if (status === 'Planifié') {
    return 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300';
  }

  if (status === 'Active') {
    return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300';
  }

  if (status === 'Inactive') {
    return 'bg-slate-100 text-slate-700 dark:bg-slate-500/10 dark:text-slate-300';
  }

  return 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300';
}

function compareSalonStatus(left: SalonRecord['status'], right: SalonRecord['status']) {
  const order = {
    Planifié: 0,
    Active: 0,
    Inactive: 2,
    Terminé: 3,
  } satisfies Record<SalonRecord['status'], number>;

  return order[left] - order[right];
}

function exportSalonLeadsAsExcel(salon: SalonRecord, region: RegionRecord, leads: LeadRecord[]) {
  if (leads.length === 0) {
    toast.error('Aucun contact à exporter pour ce salon.');
    return;
  }

  downloadLeadsAsXlsx(
    leads,
    `salon-${region.code.toLowerCase()}-${salon.name.toLowerCase().replaceAll(/\s+/g, '-')}.xlsx`,
  );
  toast.success(`Export du salon ${salon.name} généré.`);
}

function StatCard({ label, value, icon, subtext }: { icon: ReactNode; label: string; subtext?: string; value: string | number }) {
  return (
    <div className="rounded-[2rem] border border-border bg-card p-6 shadow-sm transition-shadow hover:shadow-md">
      <div className="mb-4 flex items-center justify-between">
        <span className="text-sm font-medium tracking-tight text-muted-foreground lowercase">{label}</span>
        <div className="rounded-xl bg-muted p-2">{icon}</div>
      </div>
      <div className="space-y-1">
        <div className="text-3xl font-bold text-foreground">{value}</div>
        {subtext ? <p className="text-[10px] font-semibold uppercase text-muted-foreground">{subtext}</p> : null}
      </div>
    </div>
  );
}

export function SalonManagementPage() {
  const { data: session } = authClient.useSession();
  const { id: regionId } = useParams<{ id: string }>();
  const [region, setRegion] = useState<RegionRecord | null>(null);
  const [salons, setSalons] = useState<SalonRecord[]>([]);
  const [leads, setLeads] = useState<LeadRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [view, setView] = useState<'edit' | 'list' | 'new' | 'stats'>('list');
  const [selectedSalon, setSelectedSalon] = useState<SalonRecord | null>(null);
  const [selectedStats, setSelectedStats] = useState<SalonStats | null>(null);
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [tableColumns, setTableColumns] = useState<typeof defaultTableColumns>(() => readStoredTableColumns());
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<SalonListStatusFilter>('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [sortKey, setSortKey] = useState<SalonSortKey>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof pageSizeOptions)[number]>(20);
  const [confirmState, setConfirmState] = useState<ConfirmState>({
    confirmText: '',
    description: '',
    isOpen: false,
    onConfirm: async () => {},
    title: '',
  });

  const isCommercial = (session?.user as { role?: string | null } | undefined)?.role === 'user';

  const fetchData = useCallback(async () => {
    if (!regionId) {
      return;
    }

    setIsLoading(true);

    try {
      const { data: regionData } = await (isCommercial ? listUserRegions() : listRegions());
      if (regionData?.data) {
        setRegion(regionData.data.find((item) => item.id === regionId) ?? null);
      }

      const [salonsResult, leadsResult] = await Promise.all([
        listSalonsByRegion(regionId),
        isCommercial ? listUserLeads() : listLeadsByRegion(regionId),
      ]);

      if (salonsResult.data) {
        setSalons(salonsResult.data);
      }

      if (leadsResult.data?.data) {
        setLeads(leadsResult.data.data.filter((lead) => lead.regionId === regionId));
      } else {
        setLeads([]);
      }

    } catch (error) {
      console.error(error);
      toast.error('Erreur lors du chargement des salons.');
    } finally {
      setIsLoading(false);
    }
  }, [isCommercial, regionId]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(SALONS_TABLE_COLUMNS_STORAGE_KEY, JSON.stringify(tableColumns));
  }, [tableColumns]);

  const typeOptions = useMemo(() => {
    const salonTypes = salons
      .map((salon) => salon.type)
      .filter((value): value is NonNullable<SalonRecord['type']> => value != null);

    return Array.from(
      new Set(salonTypes),
    ).sort((left, right) => left.localeCompare(right, 'fr', { sensitivity: 'base' }));
  }, [salons]);

  const statusFilterOptions = [
    { label: 'Tous les statuts', value: 'all' },
    { label: 'Planifiés', value: 'Planifié' },
    { label: 'Actifs', value: 'Active' },
    { label: 'Inactifs', value: 'Inactive' },
    { label: 'Terminés', value: 'Terminé' },
  ];

  const typeFilterOptions = [
    { label: 'Tous les types', value: 'all' },
    { label: 'Type non renseigné', value: 'Non renseigné' },
    ...typeOptions.map((typeOption) => ({ label: typeOption, value: typeOption })),
  ];

  const pageSizeFilterOptions = pageSizeOptions.map((option) => ({
    label: `${option} lignes`,
    value: String(option),
  }));

  const salonMetrics = useMemo(() => {
    return new Map<string, SalonMetrics>(
      salons.map((salon) => {
        const contactsCollected = leads.filter((lead) => lead.salonId === salon.id).length;
        const lot1Distributed = Math.max(0, salon.lot1Qty - salon.lot1Remaining);
        const lot2Distributed = Math.max(0, salon.lot2Qty - salon.lot2Remaining);

        return [
          salon.id,
          {
            contactsCollected,
            lot1Distributed,
            lot2Distributed,
            lotsDedicated: salon.lot1Qty + salon.lot2Qty,
            lotsWon: lot1Distributed + lot2Distributed,
          },
        ];
      }),
    );
  }, [leads, salons]);

  const filteredSalons = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    return salons.filter((salon) => {
      if (statusFilter !== 'all' && salon.status !== statusFilter) {
        return false;
      }

      if (typeFilter !== 'all' && (salon.type ?? 'Non renseigné') !== typeFilter) {
        return false;
      }

      if (normalizedSearch) {
        const searchableValues = [
          salon.name,
          salon.ville ?? '',
          salon.type ?? '',
          region?.name ?? '',
        ]
          .join(' ')
          .toLowerCase();

        if (!searchableValues.includes(normalizedSearch)) {
          return false;
        }
      }

      if (periodStart) {
        const start = salon.startDate ? new Date(salon.startDate) : null;
        if (!start || start < new Date(periodStart)) {
          return false;
        }
      }

      if (periodEnd) {
        const end = salon.endDate ? new Date(salon.endDate) : salon.startDate ? new Date(salon.startDate) : null;
        if (!end || end > new Date(`${periodEnd}T23:59:59`)) {
          return false;
        }
      }

      return true;
    });
  }, [periodEnd, periodStart, region?.name, salons, searchValue, statusFilter, typeFilter]);

  const sortedSalons = useMemo(() => {
    const direction = sortDirection === 'asc' ? 1 : -1;

    return [...filteredSalons].sort((left, right) => {
      if (sortKey === 'contacts') {
        const leftContacts = salonMetrics.get(left.id)?.contactsCollected ?? 0;
        const rightContacts = salonMetrics.get(right.id)?.contactsCollected ?? 0;
        return (leftContacts - rightContacts) * direction;
      }

      if (sortKey === 'region') {
        const leftValue = region?.name ?? '';
        const rightValue = region?.name ?? '';
        return leftValue.localeCompare(rightValue, 'fr', { sensitivity: 'base' }) * direction;
      }

      if (sortKey === 'startDate') {
        const leftValue = left.startDate ? new Date(left.startDate).getTime() : 0;
        const rightValue = right.startDate ? new Date(right.startDate).getTime() : 0;
        return (leftValue - rightValue) * direction;
      }

      if (sortKey === 'type') {
        const leftValue = left.type ?? 'Non renseigné';
        const rightValue = right.type ?? 'Non renseigné';
        return leftValue.localeCompare(rightValue, 'fr', { sensitivity: 'base' }) * direction;
      }

      if (sortKey === 'lots') {
        const leftMetrics = salonMetrics.get(left.id);
        const rightMetrics = salonMetrics.get(right.id);
        const leftWon = leftMetrics?.lotsWon ?? 0;
        const rightWon = rightMetrics?.lotsWon ?? 0;

        if (leftWon !== rightWon) {
          return (leftWon - rightWon) * direction;
        }

        const leftDedicated = leftMetrics?.lotsDedicated ?? 0;
        const rightDedicated = rightMetrics?.lotsDedicated ?? 0;
        return (leftDedicated - rightDedicated) * direction;
      }

      if (sortKey === 'gainRate') {
        const leftValue = left.lot1WinPercent + left.lot2WinPercent;
        const rightValue = right.lot1WinPercent + right.lot2WinPercent;
        return (leftValue - rightValue) * direction;
      }

      if (sortKey === 'status') {
        return compareSalonStatus(left.status, right.status) * direction;
      }

      return left.name.localeCompare(right.name, 'fr', { sensitivity: 'base' }) * direction;
    });
  }, [filteredSalons, region?.name, salonMetrics, sortDirection, sortKey]);

  const pageCount = Math.max(1, Math.ceil(sortedSalons.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const paginatedSalons = sortedSalons.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const currentRangeStart = sortedSalons.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const currentRangeEnd = Math.min(currentPage * pageSize, sortedSalons.length);
  const paginationItems = getPaginationItems(currentPage, pageCount);
  const visibleTableColumns = tableColumns.filter((column) => column.visible);

  useEffect(() => {
    setPage(1);
  }, [pageSize, periodEnd, periodStart, searchValue, statusFilter, typeFilter]);

  function clearStatusFilter() {
    setStatusFilter('all');
  }

  function clearTypeFilter() {
    setTypeFilter('all');
  }

  function clearDateFilters() {
    setPeriodStart('');
    setPeriodEnd('');
  }

  function resetControls() {
    setSearchValue('');
    setStatusFilter('all');
    setTypeFilter('all');
    setPeriodStart('');
    setPeriodEnd('');
    setSortKey('name');
    setSortDirection('asc');
    setPage(1);
  }

  const activeFilters = [
    statusFilter !== 'all'
      ? {
          key: 'status',
          label: statusFilterOptions.find((option) => option.value === statusFilter)?.label ?? statusFilter,
          onClear: clearStatusFilter,
        }
      : null,
    typeFilter !== 'all'
      ? {
          key: 'type',
          label: typeFilterOptions.find((option) => option.value === typeFilter)?.label ?? typeFilter,
          onClear: clearTypeFilter,
        }
      : null,
    periodStart || periodEnd
      ? {
          key: 'dates',
          label: `Période : ${periodStart || '...'} -> ${periodEnd || '...'}`,
          onClear: clearDateFilters,
        }
      : null,
  ].filter(Boolean) as Array<{ key: string; label: string; onClear: () => void }>;

  function resetTableColumns() {
    setTableColumns(defaultTableColumns);
  }

  function toggleSort(nextKey: SalonSortKey) {
    if (sortKey === nextKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortKey(nextKey);
    setSortDirection(nextKey === 'startDate' ? 'desc' : 'asc');
  }

  function handleSortPresetChange(value: string) {
    const [nextSortKey, nextSortDirection] = value.split(':') as [SalonSortKey, SortDirection];
    setSortKey(nextSortKey);
    setSortDirection(nextSortDirection);
    setPage(1);
  }

  async function handleViewStats(salon: SalonRecord) {
    setIsActionLoading(true);

    try {
      const { data, error } = await getSalonStats(salon.id);
      if (error) {
        throw error;
      }

      if (data) {
        setSelectedStats(data);
        setSelectedSalon(salon);
        setView('stats');
      }
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors du chargement des statistiques.');
    } finally {
      setIsActionLoading(false);
    }
  }

  function handleDownloadQR(salon: SalonRecord) {
    if (!region) {
      return;
    }

    const url = `${apiBaseUrl}/api/regions/${region.code}/slots/${salon.slotNumber}/qrcode`;
    const link = document.createElement('a');
    link.href = url;
    link.download = `qrcode-${region.code.toLowerCase()}-salon-${salon.slotNumber}-${salon.name.toLowerCase().replaceAll(/\s+/g, '-')}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Téléchargement du QR code ${salon.slotNumber}`);
  }

  function handleExportSalon(salon: SalonRecord) {
    if (!region) {
      return;
    }

    exportSalonLeadsAsExcel(
      salon,
      region,
      leads.filter((lead) => lead.salonId === salon.id),
    );
  }

  function openConfirmation(title: string, description: string, confirmText: string, onConfirm: () => Promise<void>) {
    setConfirmState({
      confirmText,
      description,
      isOpen: true,
      onConfirm,
      title,
    });
  }

  function handleDeactivateSalon(salon: SalonRecord) {
    openConfirmation(
      'Désactiver le salon ?',
      `Le salon "${salon.name}" deviendra inactif et ne sera plus proposé dans le jeu tant qu il n est pas réactivé.`,
      'Désactiver',
      async () => {
        setIsActionLoading(true);
        try {
          const { error } = await updateSalon(salon.id, { status: 'Inactive' });
          if (error) throw error;
          toast.success('Salon désactivé.');
          await fetchData();
        } catch (error) {
          console.error(error);
          toast.error('Erreur lors de la désactivation du salon.');
        } finally {
          setIsActionLoading(false);
        }
      },
    );
  }

  function handleReactivateSalon(salon: SalonRecord) {
    openConfirmation(
      'Réactiver le salon ?',
      `Le salon "${salon.name}" redeviendra disponible dans cette région.`,
      'Réactiver',
      async () => {
        setIsActionLoading(true);
        try {
          const shouldReactivateAsActive = salon.startDate ? new Date(salon.startDate).getTime() <= Date.now() : false;
          const { error } = await updateSalon(salon.id, { status: shouldReactivateAsActive ? 'Active' : 'Planifié' });
          if (error) throw error;
          toast.success('Salon réactivé.');
          await fetchData();
        } catch (error) {
          console.error(error);
          toast.error('Erreur lors de la réactivation du salon.');
        } finally {
          setIsActionLoading(false);
        }
      },
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2Icon className="size-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!region) {
    return (
      <div className="py-24 text-center">
        <p className="text-muted-foreground">Région non trouvée.</p>
        <Button asChild variant="link" className="mt-2">
          <Link to="/admin/regions">Retourner à la liste</Link>
        </Button>
      </div>
    );
  }

  if (view === 'new' || view === 'edit') {
    return (
      <div className="mx-auto max-w-4xl space-y-8 pb-20">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setView('list')}
              className="-ml-2 mb-2 text-muted-foreground hover:text-foreground"
            >
              <ChevronLeftIcon className="mr-1 size-4" />
              Retour à la liste
            </Button>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              {view === 'new' ? 'Ajouter un salon' : 'Modifier un salon'}
            </h1>
            <p className="text-muted-foreground">
              {view === 'new'
                ? `Ajoutez un salon pour la région ${region.name} et configurez son jeu.`
                : `Modification du salon ${selectedSalon?.name ?? 'sélectionné'}.`}
            </p>
          </div>
        </div>

        <SalonForm
          regionId={regionId!}
          existingSalons={salons}
          salon={view === 'edit' ? selectedSalon ?? undefined : undefined}
          onSuccess={() => {
            setView('list');
            void fetchData();
          }}
          onCancel={() => setView('list')}
        />
      </div>
    );
  }

  if (view === 'stats' && selectedStats) {
    return (
      <section className="mx-auto max-w-4xl space-y-8 pb-20">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setView('list')}
              className="-ml-2 mb-2 text-muted-foreground hover:text-foreground"
            >
              <ChevronLeftIcon className="mr-1 size-4" />
              Retour à la liste
            </Button>
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">
              Statistiques du salon
            </h1>
            <p className="text-sm text-muted-foreground">
              {selectedStats.salon.name} · {region.name}
            </p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total leads" value={selectedStats.totalLeads} icon={<PlusIcon className="size-4" />} />
          <StatCard
            label="Gagnants"
            value={selectedStats.totalWinners}
            icon={<CheckCircle2Icon className="size-4 text-green-500" />}
            subtext={`${selectedStats.transformationRate}% de conversion`}
          />
          <StatCard
            label="Lot 1 distribués"
            value={selectedStats.lot1Distributed}
            icon={<Badge className="flex size-4 items-center justify-center p-0">1</Badge>}
            subtext={`Initial : ${selectedStats.salon.lot1Qty}`}
          />
          <StatCard
            label="Lot 2 distribués"
            value={selectedStats.lot2Distributed}
            icon={<Badge variant="secondary" className="flex size-4 items-center justify-center p-0">2</Badge>}
            subtext={`Initial : ${selectedStats.salon.lot2Qty}`}
          />
        </div>

        <div className="space-y-6 rounded-[2.5rem] border border-border bg-card p-8">
          <h3 className="flex items-center gap-2 text-xl font-bold">
            <BarChart3Icon className="size-5 text-primary" />
            Détails des leads
          </h3>
          <div className="flex items-center justify-center rounded-2xl border-2 border-dashed bg-muted/10 py-12 text-center text-muted-foreground italic">
            Le listing détaillé des leads reste disponible dans le menu "Leads" pour l export complet.
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="flex min-h-[calc(100svh-10rem)] w-full flex-col gap-8 pb-20">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">Salons</h1>
            <Badge variant="outline" className="border-primary/20 bg-primary/5 px-3 py-1 text-primary">
              {region.name}
            </Badge>
          </div>
          <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
            Consulte, filtre et pilote les salons de cette région avec leurs contacts collectés et leurs dotations.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => setView('new')}
            className="h-11 border-black bg-black px-5 text-white shadow-none transition-colors hover:border-black/85 hover:bg-black/85 hover:text-white dark:border-border dark:bg-muted dark:text-foreground dark:hover:border-foreground/20 dark:hover:bg-muted/80"
          >
            <PlusIcon className="mr-2 size-4" />
            Ajouter un salon
          </Button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[2rem] border border-border bg-card">
        <div className="border-b border-border/70 bg-[#f4f4f538] p-4 sm:p-5 dark:bg-muted/40">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex w-full max-w-[42rem] items-center gap-2">
              <div className="relative min-w-0 flex-1 max-w-[34rem]">
                <SearchIcon className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  placeholder="Rechercher un salon, une ville, un type..."
                  className="h-12 border-border bg-background pl-11 pr-11 text-[15px] shadow-none placeholder:text-muted-foreground dark:bg-muted"
                />
                {searchValue ? (
                  <button
                    type="button"
                    aria-label="Effacer la recherche"
                    onClick={() => setSearchValue('')}
                    className="absolute top-1/2 right-3 inline-flex size-7 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <XIcon className="size-4" />
                  </button>
                ) : null}
              </div>

              <ToolbarButton onClick={() => setIsFilterSheetOpen(true)} className="shrink-0 gap-2 px-4">
                Filtres
                <Settings2Icon className="size-4" />
              </ToolbarButton>
            </div>

            <div className="flex items-center justify-end gap-2 xl:ml-auto">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <ToolbarButton className="justify-between pr-6 xl:min-w-56">
                    {getSortPresetLabel(sortKey, sortDirection)}
                    <ChevronDownIcon className="size-4" />
                  </ToolbarButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>Ordre d’affichage</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuRadioGroup value={`${sortKey}:${sortDirection}`} onValueChange={handleSortPresetChange}>
                    {sortPresetOptions.map((option) => (
                      <DropdownMenuRadioItem key={option.value} value={option.value}>
                        {option.label}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>

              <UsersTableColumnsPopover
                columns={tableColumns}
                onChange={(columns) => setTableColumns(columns as typeof defaultTableColumns)}
                onReset={resetTableColumns}
              />
            </div>
          </div>

          {activeFilters.length > 0 ? (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {activeFilters.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  onClick={filter.onClear}
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:border-foreground/20 hover:bg-muted dark:bg-muted dark:hover:bg-muted/80"
                >
                  <span>{filter.label}</span>
                  <XIcon className="size-3.5 text-muted-foreground" />
                </button>
              ))}

              {activeFilters.length >= 3 ? (
                <button
                  type="button"
                  onClick={resetControls}
                  className="text-sm font-medium text-black underline underline-offset-4 transition-colors hover:text-black/80 dark:text-foreground dark:hover:text-foreground/80"
                >
                  Effacer tout
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          <table className="min-w-full border-separate border-spacing-0 text-left text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                {visibleTableColumns.map((column) => (
                  <th key={column.key} className="px-6 py-4">
                    {column.key === 'name' ? (
                      <SortHeaderButton active={sortKey === 'name'} direction={sortDirection} onClick={() => toggleSort('name')}>
                        {column.label}
                      </SortHeaderButton>
                    ) : column.key === 'date' ? (
                      <SortHeaderButton active={sortKey === 'startDate'} direction={sortDirection} onClick={() => toggleSort('startDate')}>
                        {column.label}
                      </SortHeaderButton>
                    ) : column.key === 'contacts' ? (
                      <SortHeaderButton active={sortKey === 'contacts'} direction={sortDirection} onClick={() => toggleSort('contacts')}>
                        {column.label}
                      </SortHeaderButton>
                    ) : column.key === 'region' ? (
                      <SortHeaderButton active={sortKey === 'region'} direction={sortDirection} onClick={() => toggleSort('region')}>
                        {column.label}
                      </SortHeaderButton>
                    ) : column.key === 'type' ? (
                      <SortHeaderButton active={sortKey === 'type'} direction={sortDirection} onClick={() => toggleSort('type')}>
                        {column.label}
                      </SortHeaderButton>
                    ) : column.key === 'lots' ? (
                      <SortHeaderButton active={sortKey === 'lots'} direction={sortDirection} onClick={() => toggleSort('lots')}>
                        {column.label}
                      </SortHeaderButton>
                    ) : column.key === 'gainRate' ? (
                      <SortHeaderButton active={sortKey === 'gainRate'} direction={sortDirection} onClick={() => toggleSort('gainRate')}>
                        {column.label}
                      </SortHeaderButton>
                    ) : column.key === 'status' ? (
                      <SortHeaderButton active={sortKey === 'status'} direction={sortDirection} onClick={() => toggleSort('status')}>
                        {column.label}
                      </SortHeaderButton>
                    ) : (
                      <span className="font-semibold text-foreground">{column.label}</span>
                    )}
                  </th>
                ))}
                <th className="px-6 py-4 text-right font-semibold text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSalons.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-16 text-center text-muted-foreground italic">
                    Aucun salon ne correspond aux filtres actuels.
                  </td>
                </tr>
              ) : (
                paginatedSalons.map((salon) => {
                  const metrics = salonMetrics.get(salon.id) ?? {
                    contactsCollected: 0,
                    lot1Distributed: 0,
                    lot2Distributed: 0,
                    lotsDedicated: salon.lot1Qty + salon.lot2Qty,
                    lotsWon: 0,
                  };
                  const canEditSalon = salon.status !== 'Terminé';

                  return (
                    <tr
                      key={salon.id}
                      className={cn(
                        'group transition-colors',
                        canEditSalon ? 'cursor-pointer hover:bg-accent/10' : 'hover:bg-accent/10',
                      )}
                      onClick={canEditSalon ? () => {
                        setSelectedSalon(salon);
                        setView('edit');
                      } : undefined}
                    >
                      {visibleTableColumns.map((column) => (
                        <td
                          key={column.key}
                          className={cn(
                            'border-t border-border px-6 py-5 align-top transition-colors group-hover:bg-accent/10',
                            column.key === 'date' ? 'min-w-[15rem]' : undefined,
                          )}
                        >
                          {column.key === 'name' ? (
                            <div className="space-y-1">
                              <div className="font-semibold text-foreground">{salon.name}</div>
                              <div className="text-xs text-muted-foreground">{salon.ville || 'Ville non renseignée'}</div>
                            </div>
                          ) : column.key === 'date' ? (
                            <div className="inline-flex min-w-[13rem] items-center gap-2 whitespace-nowrap text-muted-foreground">
                              <CalendarIcon className="size-4" />
                              {formatDateRange(salon.startDate, salon.endDate)}
                            </div>
                          ) : column.key === 'region' ? (
                            <span className="text-foreground">{region.name}</span>
                          ) : column.key === 'contacts' ? (
                            <span className="text-foreground">{metrics.contactsCollected}</span>
                          ) : column.key === 'type' ? (
                            <span className="text-foreground">{salon.type ?? 'Non renseigné'}</span>
                          ) : column.key === 'lots' ? (
                            <span className="text-foreground">{metrics.lotsWon}/{metrics.lotsDedicated}</span>
                          ) : column.key === 'gainRate' ? (
                            <span className="text-foreground">Lot 1: {salon.lot1WinPercent}% · Lot 2: {salon.lot2WinPercent}%</span>
                          ) : (
                            <span className={cn('inline-flex rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wider', getSalonStatusClasses(salon.status))}>
                              {formatSalonStatus(salon.status)}
                            </span>
                          )}
                        </td>
                      ))}
                      <td className="border-t border-border px-6 py-5 align-top transition-colors group-hover:bg-accent/10">
                        <div className="flex items-center justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="icon-sm"
                                className="h-10 w-10 border-border bg-background text-foreground hover:border-foreground/20 hover:bg-muted dark:bg-muted dark:hover:bg-muted/80"
                                aria-label={`Actions pour ${salon.name}`}
                                onClick={(event) => event.stopPropagation()}
                              >
                                <EllipsisIcon className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-60">
                              <DropdownMenuLabel>Actions salon</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              {salon.status !== 'Terminé' ? (
                                <DropdownMenuItem onClick={(event) => {
                                  event.stopPropagation();
                                  setSelectedSalon(salon);
                                  setView('edit');
                                }}>
                                  <PencilIcon />
                                  Modifier
                                </DropdownMenuItem>
                              ) : null}
                              {(salon.status === 'Active' || salon.status === 'Planifié') ? (
                                <DropdownMenuItem onClick={(event) => {
                                  event.stopPropagation();
                                  handleDownloadQR(salon);
                                }}>
                                  <QrCodeIcon />
                                  Télécharger le QR code
                                </DropdownMenuItem>
                              ) : null}
                              {salon.status === 'Terminé' ? (
                                <>
                                  <DropdownMenuItem disabled={isActionLoading} onClick={(event) => {
                                    event.stopPropagation();
                                    void handleViewStats(salon);
                                  }}>
                                    <BarChart3Icon />
                                    Voir les statistiques
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={(event) => {
                                    event.stopPropagation();
                                    handleExportSalon(salon);
                                  }}>
                                    <DownloadIcon />
                                    Exporter
                                  </DropdownMenuItem>
                                </>
                              ) : null}
                              {salon.status === 'Active' ? (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={(event) => {
                                    event.stopPropagation();
                                    handleDeactivateSalon(salon);
                                  }}>
                                    Désactiver
                                  </DropdownMenuItem>
                                </>
                              ) : null}
                              {salon.status === 'Inactive' ? (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={(event) => {
                                    event.stopPropagation();
                                    handleReactivateSalon(salon);
                                  }}>
                                    Réactiver
                                  </DropdownMenuItem>
                                </>
                              ) : null}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div id="salons-pagination" className="grid gap-3 border-t border-border px-6 py-4 md:grid-cols-[1fr_auto_1fr] md:items-center">
          <p className="text-sm text-muted-foreground">
            {currentRangeStart}-{currentRangeEnd} sur {sortedSalons.length} salons affichés
          </p>

          <Pagination className="justify-center">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#salons-pagination"
                  aria-disabled={currentPage <= 1}
                  tabIndex={currentPage <= 1 ? -1 : 0}
                  className={cn(currentPage <= 1 ? 'pointer-events-none opacity-50' : undefined)}
                  onClick={(event) => {
                    event.preventDefault();
                    if (currentPage <= 1) return;
                    setPage((value) => value - 1);
                  }}
                />
              </PaginationItem>

              {paginationItems.map((pageNumber, index) => {
                const previousPage = paginationItems[index - 1];
                const shouldRenderEllipsis = previousPage && pageNumber - previousPage > 1;

                return (
                  <Fragment key={pageNumber}>
                    {shouldRenderEllipsis ? (
                      <PaginationItem>
                        <PaginationEllipsis />
                      </PaginationItem>
                    ) : null}
                    <PaginationItem>
                      <PaginationLink
                        href="#salons-pagination"
                        isActive={pageNumber === currentPage}
                        onClick={(event) => {
                          event.preventDefault();
                          setPage(pageNumber);
                        }}
                      >
                        {pageNumber}
                      </PaginationLink>
                    </PaginationItem>
                  </Fragment>
                );
              })}

              <PaginationItem>
                <PaginationNext
                  href="#salons-pagination"
                  aria-disabled={currentPage >= pageCount}
                  tabIndex={currentPage >= pageCount ? -1 : 0}
                  className={cn(currentPage >= pageCount ? 'pointer-events-none opacity-50' : undefined)}
                  onClick={(event) => {
                    event.preventDefault();
                    if (currentPage >= pageCount) return;
                    setPage((value) => value + 1);
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>

          <div className="flex justify-start md:justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <ToolbarButton className="h-10 justify-between pl-4 pr-5 text-sm">
                  {pageSize} lignes
                  <ChevronDownIcon className="size-4" />
                </ToolbarButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Lignes par page</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup value={String(pageSize)} onValueChange={(value) => setPageSize(Number(value) as (typeof pageSizeOptions)[number])}>
                  {pageSizeFilterOptions.map((option) => (
                    <DropdownMenuRadioItem key={option.value} value={option.value}>
                      {option.label}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        onClose={() => setConfirmState((current) => ({ ...current, isOpen: false }))}
        onConfirm={async () => {
          await confirmState.onConfirm();
          setConfirmState((current) => ({ ...current, isOpen: false }));
        }}
        title={confirmState.title}
        description={confirmState.description}
        confirmText={confirmState.confirmText}
        variant="destructive"
      />

      <Sheet open={isFilterSheetOpen} onOpenChange={setIsFilterSheetOpen}>
        <SheetContent side="right" className="w-full border-border sm:max-w-md">
          <SheetHeader className="border-b border-border">
            <SheetTitle>Filtres</SheetTitle>
            <SheetDescription>
              Ajustez le statut, le type et la période des salons sans quitter la table.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 space-y-8 overflow-y-auto px-6 py-6">
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">Statut</h3>
              <div className="flex flex-wrap gap-2">
                {statusFilterOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setStatusFilter(option.value as SalonListStatusFilter)}
                    className={cn(
                      'rounded-full border px-4 py-2 text-sm transition-colors',
                      statusFilter === option.value
                        ? 'border-black bg-black text-white'
                        : 'border-border bg-background text-foreground hover:border-foreground/20 hover:bg-muted',
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">Type</h3>
              <div className="flex flex-wrap gap-2">
                {typeFilterOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setTypeFilter(option.value)}
                    className={cn(
                      'rounded-full border px-4 py-2 text-sm transition-colors',
                      typeFilter === option.value
                        ? 'border-black bg-black text-white'
                        : 'border-border bg-background text-foreground hover:border-foreground/20 hover:bg-muted',
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">Période</h3>
              <div className="grid gap-3">
                <Input type="date" value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} />
                <Input type="date" value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} />
              </div>
            </div>
          </div>

          <SheetFooter className="border-t border-border">
            <Button variant="outline" onClick={resetControls}>
              Réinitialiser
            </Button>
            <Button onClick={() => setIsFilterSheetOpen(false)}>Appliquer</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </section>
  );
}
